/**
 * `<ab-rotator>`: shows one `[data-ab-rotate-item]` child at a time, cycling every
 * `data-interval` seconds. Pauses while hovered or focused and under reduced motion.
 */
class AbRotator extends HTMLElement {
  #timer = 0;
  #paused = false;
  #controller = new AbortController();

  connectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const items = [...this.querySelectorAll('[data-ab-rotate-item]')];
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (items.length < 2 || reduceMotion) return;

    for (const type of ['pointerenter', 'focusin']) this.addEventListener(type, () => (this.#paused = true), { signal });
    for (const type of ['pointerleave', 'focusout']) this.addEventListener(type, () => (this.#paused = false), { signal });

    let index = 0;
    const seconds = Number(this.dataset.interval) || 5;
    this.#timer = window.setInterval(() => {
      if (this.#paused || document.hidden) return;
      items[index].hidden = true;
      index = (index + 1) % items.length;
      items[index].hidden = false;
    }, seconds * 1000);
  }

  disconnectedCallback() {
    this.#controller.abort();
    window.clearInterval(this.#timer);
  }
}

if (!customElements.get('ab-rotator')) customElements.define('ab-rotator', AbRotator);

/**
 * Opens and closes Aromas de Brisa bottom-sheet dialogs.
 *
 * `[data-ab-open-sheet="<dialog id>"]` opens the dialog as a modal; `[data-ab-close-sheet]`
 * and clicks on the backdrop close it. Native `<dialog>` handles Escape and focus return.
 */
if (!window.abSheetReady) {
  window.abSheetReady = true;

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const opener = event.target.closest('[data-ab-open-sheet]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('data-ab-open-sheet') || '');
      if (dialog instanceof HTMLDialogElement && !dialog.open) {
        dialog.showModal();
        opener.setAttribute('aria-expanded', 'true');
        dialog.addEventListener('close', () => opener.setAttribute('aria-expanded', 'false'), { once: true });
      }
      return;
    }

    if (event.target.closest('[data-ab-close-sheet]')) {
      event.target.closest('dialog')?.close();
      return;
    }

    // A click whose target is the dialog element itself landed on the backdrop.
    if (event.target instanceof HTMLDialogElement && event.target.matches('.ab-sheet')) {
      event.target.close();
    }
  });
}
